import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Stars, Lock, Loader2 } from "lucide-react";
import { SignupPlanPicker } from "@/components/SignupPlanPicker";
import { AuthShell, Field, inputClass } from "@/components/AuthShell";
import { GoogleButton, OrDivider } from "@/components/GoogleButton";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { STATES } from "@/pages/Onboarding";

const LANGS = ["English", "Hindi", "Tamil", "Telugu", "Kannada", "Malayalam", "Bengali", "Marathi", "Gujarati"];

export default function Register() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [gstPct, setGstPct] = useState(18);
  const [planId, setPlanId] = useState(params.get("plan") || "monthly");
  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/config").then((r) => setGstPct(r.data.gst_percent ?? 18)).catch(() => {});
  }, []);
  const plan = plans.find((p) => p.id === planId);
  const total = plan ? +(plan.price * (1 + gstPct / 100)).toFixed(2) : 0;
  const [f, setF] = useState({
    name: "", email: "", phone: "", password: "", gender: "",
    preferred_language: "English",
    date_of_birth: "", time_of_birth: "", birth_city: "", birth_state: "",
    whatsapp_consent: true, marketing_consent: false, terms_consent: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setCheck = (k) => (e) => setF({ ...f, [k]: e.target.checked });

  const [unknownTime, setUnknownTime] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.terms_consent) { setError("Please agree to the Terms & Privacy Policy."); return; }
    if (!f.date_of_birth || !f.birth_city.trim() || !f.birth_state) { setError("Please add your date and place of birth for an accurate chart."); return; }
    if (!unknownTime && !f.time_of_birth) { setError("Please add your time of birth, or tick “I don't know my exact time”."); return; }
    setLoading(true);
    let opened = false;
    try {
      const { data: o } = await api.post("/signup/start", {
        ...f, plan_id: planId, email: f.email.trim().toLowerCase(), birth_city: f.birth_city.trim(),
        time_of_birth: unknownTime ? "12:00" : f.time_of_birth,
        place_of_birth: `${f.birth_city.trim()}, ${f.birth_state}`,
      });
      const finish = async (pay) => {
        const { data } = await api.post("/signup/complete", { signup_id: o.signup_id, ...pay });
        setUser(data.user);
        toast.success("Payment successful — your account is ready ✨");
        nav("/welcome");
      };
      if (o.provider === "razorpay") {
        await new Promise((res) => { const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js"; s.onload = res; s.onerror = res; document.body.appendChild(s); });
        if (!window.Razorpay) throw new Error("Could not load payment gateway");
        const rzp = new window.Razorpay({
          key: o.key_id, subscription_id: o.subscription_id, name: "MyssticChat",
          description: `${o.plan?.name} plan`, prefill: { name: f.name, email: f.email, contact: f.phone }, theme: { color: "#E5C158" },
          handler: async (r) => {
            try { await finish({ razorpay_payment_id: r.razorpay_payment_id, razorpay_subscription_id: r.razorpay_subscription_id, razorpay_signature: r.razorpay_signature }); }
            catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed"); }
            finally { setLoading(false); }
          },
          modal: { ondismiss: () => setLoading(false) },
        });
        rzp.open();
        opened = true;
        return;
      }
      await new Promise((r) => setTimeout(r, 1200));
      await finish({ razorpay_order_id: o.order_id, razorpay_payment_id: `pay_mock_${Date.now()}`, razorpay_signature: "mock_signature" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      if (!opened) setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create Your Account"
      subtitle="Begin your personalized monthly astrology journey"
      footer={<>Already a member? <Link to="/login" className="text-amber-300 hover:text-amber-200">Sign in</Link></>}
    >
      <GoogleButton />
      <OrDivider />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Full Name">
          <input data-testid="register-name-input" required value={f.name} onChange={set("name")} className={inputClass} placeholder="Your full name" />
        </Field>
        <Field label="Email Address">
          <input data-testid="register-email-input" type="email" required value={f.email} onChange={set("email")} className={inputClass} placeholder="you@example.com" />
        </Field>
        <Field label="Mobile Number">
          <input data-testid="register-phone-input" required value={f.phone} onChange={set("phone")} className={inputClass} placeholder="+91 98765 43210" />
        </Field>
        <Field label="Password">
          <input data-testid="register-password-input" type="password" required minLength={6} value={f.password} onChange={set("password")} className={inputClass} placeholder="Min 6 characters" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Gender (optional)">
            <select data-testid="register-gender-select" value={f.gender} onChange={set("gender")} className={inputClass}>
              <option value="">Prefer not to say</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Language">
            <select data-testid="register-language-select" value={f.preferred_language} onChange={set("preferred_language")} className={inputClass}>
              {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
        </div>

        <fieldset className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.03] p-4 space-y-3" data-testid="register-birth-section">
          <legend className="px-2 text-xs uppercase tracking-[0.15em] text-amber-300 flex items-center gap-1.5"><Stars size={13} /> Birth Details</legend>
          <p className="text-[11px] text-slate-400 -mt-1">Used to calculate your Vedic birth chart for accurate predictions from day one.</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date of Birth">
              <input data-testid="register-dob-input" type="date" required max={today} value={f.date_of_birth} onChange={set("date_of_birth")} className={inputClass} />
            </Field>
            <Field label="Time of Birth">
              <input data-testid="register-tob-input" type="time" disabled={unknownTime} required={!unknownTime} value={unknownTime ? "" : f.time_of_birth} onChange={set("time_of_birth")} className={`${inputClass} disabled:opacity-40`} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-[11px] text-slate-400 -mt-1">
            <input data-testid="register-unknown-time" type="checkbox" checked={unknownTime} onChange={(e) => setUnknownTime(e.target.checked)} className="accent-amber-400" />
            I don't know my exact time (we'll use 12:00 noon)
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Birth City / Town">
              <input data-testid="register-birth-city-input" required value={f.birth_city} onChange={set("birth_city")} className={inputClass} placeholder="e.g. Kochi" />
            </Field>
            <Field label="State">
              <select data-testid="register-birth-state-select" required value={f.birth_state} onChange={set("birth_state")} className={inputClass}>
                <option value="">Select state</option>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
        </fieldset>

        <SignupPlanPicker plans={plans} selected={planId} onSelect={setPlanId} gstPct={gstPct} />

        <div className="space-y-2.5 pt-1">
          <label className="flex items-start gap-2.5 text-xs text-slate-300 leading-relaxed">
            <input data-testid="register-terms-checkbox" type="checkbox" checked={f.terms_consent} onChange={setCheck("terms_consent")} className="mt-0.5 accent-amber-400" />
            <span>I agree to the <Link to="/legal/terms" className="text-amber-300">Terms &amp; Conditions</Link> and <Link to="/legal/privacy" className="text-amber-300">Privacy Policy</Link>.</span>
          </label>
          <label className="flex items-start gap-2.5 text-xs text-slate-300 leading-relaxed">
            <input data-testid="register-whatsapp-checkbox" type="checkbox" checked={f.whatsapp_consent} onChange={setCheck("whatsapp_consent")} className="mt-0.5 accent-amber-400" />
            <span>I agree to receive my MyssticChat predictions and service notifications through WhatsApp.</span>
          </label>
          <label className="flex items-start gap-2.5 text-xs text-slate-300 leading-relaxed">
            <input data-testid="register-marketing-checkbox" type="checkbox" checked={f.marketing_consent} onChange={setCheck("marketing_consent")} className="mt-0.5 accent-amber-400" />
            <span>Send me occasional offers and astrology tips (optional).</span>
          </label>
        </div>

        {error && <p data-testid="register-error" className="text-sm text-red-400">{error}</p>}
        <button data-testid="register-submit-button" type="submit" disabled={loading}
          className="btn-gold rounded-full w-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <><Loader2 size={18} className="animate-spin" /> Processing payment…</> : <><Lock size={16} /> Pay ₹{total.toFixed(2)} & Create Account</>}
        </button>
        <p className="text-[11px] text-slate-500 text-center -mt-1" data-testid="register-pay-note">Secure payment via Razorpay (demo mode). {plan ? `${plan.name} plan · ₹${plan.price} + GST` : ""}</p>
        <p className="text-[11px] text-slate-500 text-center leading-relaxed">
          Astrology guidance is for personal reflection and entertainment, and is not medical, legal or financial advice.
        </p>
      </form>
    </AuthShell>
  );
}
